import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Alert,
  Chip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Speed as SpeedIcon,
  Computer as ComputerIcon,
  NetworkCheck as NetworkIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Settings as SettingsIcon,
  Router as RouterIcon,
  Build as BuildIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import apiService from '../services/api';

const SystemTestPage = () => {
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState({});
  const [systemStatus, setSystemStatus] = useState({});
  const [selectedTests, setSelectedTests] = useState([]);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [integrationTestDialogOpen, setIntegrationTestDialogOpen] = useState(false);
  const [terminalIntegrationDialogOpen, setTerminalIntegrationDialogOpen] = useState(false);
  const [integrationTestData, setIntegrationTestData] = useState({
    networkName: 'test-network',
    vmName: 'test-vm',
    selectedNetwork: 'default',
    terminalTestVM: ''
  });

  // 포트 포워딩 진단 다이얼로그
  const [portForwardDiagOpen, setPortForwardDiagOpen] = useState(false);
  const [portForwardDiagLoading, setPortForwardDiagLoading] = useState(false);
  const [portForwardDiagResult, setPortForwardDiagResult] = useState(null);
  const [portForwardDiagError, setPortForwardDiagError] = useState(null);

  const { enqueueSnackbar } = useSnackbar();

  // 테스트 항목 정의
  const testCategories = {
    system: {
      name: '시스템 기본',
      icon: <SettingsIcon />,
      color: 'primary',
      tests: [
        {
          id: 'health-check',
          name: 'Health Check',
          description: '시스템 상태 확인',
          function: async () => {
            // PUBLIC_URL을 고려한 health 엔드포인트
            const publicUrl = process.env.PUBLIC_URL || '';
            const response = await fetch(`${publicUrl}/health`);
            const text = await response.text();
            try {
              return JSON.parse(text);
            } catch {
              return { status: text, libvirt: 'unknown' };
            }
          },
          critical: true
        },
        {
          id: 'host-info',
          name: '호스트 정보',
          description: '호스트 설정 정보 조회',
          function: apiService.getHostInfo,
          critical: true
        }
      ]
    },
    network: {
      name: '네트워크 관리',
      icon: <NetworkIcon />,
      color: 'success',
      tests: [
        {
          id: 'network-list',
          name: '네트워크 목록',
          description: '네트워크 프로필 목록 조회',
          function: apiService.getNetworks,
          critical: true
        },
        {
          id: 'network-create',
          name: '네트워크 생성',
          description: '테스트 네트워크 생성',
          function: () => apiService.createNetwork({
            name: 'test-network-' + Date.now(),
            network_type: 'bridge',
            cidr: '192.168.200.0/24',
            description: 'Test network for system validation'
          }),
          critical: false,
          cleanup: true
        }
      ]
    },
    vm: {
      name: '가상머신 관리',
      icon: <ComputerIcon />,
      color: 'info',
      tests: [
        {
          id: 'vm-list',
          name: 'VM 목록',
          description: '가상머신 목록 조회',
          function: () => apiService.getVMs(true),
          critical: true
        },
        {
          id: 'vm-status',
          name: 'VM 상태 체크',
          description: '각 VM의 상태 확인',
          function: async () => {
            const vms = await apiService.getVMs(true);
            const statusChecks = await Promise.all(
              vms.data.vms.slice(0, 3).map(async (vm) => {
                try {
                  const status = await apiService.getVMStatus(vm.name);
                  return { vm: vm.name, status: status.data };
                } catch (error) {
                  return { vm: vm.name, error: error.message };
                }
              })
            );
            return { statusChecks };
          },
          critical: false
        }
      ]
    },
    gpu: {
      name: 'GPU 관리',
      icon: <MemoryIcon />,
      color: 'warning',
      tests: [
        {
          id: 'gpu-list',
          name: 'GPU 목록',
          description: 'GPU 장치 목록 조회',
          function: () => apiService.getGPUs(true),
          critical: true
        },
        {
          id: 'gpu-available',
          name: '사용 가능한 GPU',
          description: '할당 가능한 GPU 목록',
          function: apiService.getAvailableGPUs,
          critical: false
        }
      ]
    },
    storage: {
      name: '스토리지 관리',
      icon: <StorageIcon />,
      color: 'secondary',
      tests: [
        {
          id: 'os-images',
          name: 'OS 이미지',
          description: 'OS 이미지 목록 조회',
          function: apiService.getOSImages,
          critical: true
        },
        {
          id: 'volumes',
          name: '디스크 볼륨',
          description: '디스크 볼륨 목록 조회',
          function: apiService.getVolumes,
          critical: false
        }
      ]
    },
    portForwarding: {
      name: '포트 포워딩',
      icon: <RouterIcon />,
      color: 'error',
      tests: [
        {
          id: 'pf-rules-count',
          name: '규칙 통계',
          description: '전체 포트 포워딩 규칙 개수 및 사용 포트 수 확인',
          function: async () => {
            const [rulesRes, usedRes] = await Promise.all([
              apiService.getAllPortForwardingRules(),
              apiService.getUsedPorts()
            ]);
            return {
              total_rules: rulesRes.data.total ?? (rulesRes.data.rules?.length || 0),
              used_ports: usedRes.data.total ?? (usedRes.data.used_ports?.length || 0)
            };
          },
          critical: true
        },
        {
          id: 'pf-diagnose',
          name: '룰 진단',
          description: 'PREROUTING/FORWARD/DOCKER-USER 체인 일관성 및 중복 검사',
          function: async () => {
            const res = await apiService.diagnosePortForwarding();
            const d = res.data || {};
            // 결과 요약을 평문으로 반환 (성공/실패는 issues 0/>0으로 판단)
            if ((d.total_issues || 0) > 0) {
              const e = new Error(
                `이슈 ${d.total_issues}건 발견 - missing_in_forward:${d.issues?.missing_in_forward?.length || 0}, ` +
                `orphan_in_forward:${d.issues?.orphan_in_forward?.length || 0}, ` +
                `missing_in_docker_user:${d.issues?.missing_in_docker_user?.length || 0}, ` +
                `duplicate_accepts:${d.issues?.duplicate_accepts?.length || 0}, ` +
                `cross_vm_collisions:${d.issues?.cross_vm_host_port_collisions?.length || 0}`
              );
              e.diagnoseData = d;
              throw e;
            }
            return d;
          },
          critical: true
        }
      ]
    },
    terminal: {
      name: '웹 터미널',
      icon: <ComputerIcon />,
      color: 'info',
      tests: [
        {
          id: 'terminal-vm-list',
          name: '터미널 접속 가능한 VM',
          description: '실행 중인 VM 목록 조회 (터미널 접속 대상)',
          function: async () => {
            const vms = await apiService.getVMs(false); // 실행 중인 VM만
            const runningVMs = vms.data.vms.filter(vm => vm.state === 1 || vm.state === 'running');
            return {
              total_vms: vms.data.vms.length,
              running_vms: runningVMs.length,
              accessible_vms: runningVMs.map(vm => ({
                name: vm.name,
                memory: vm.memory,
                vcpus: vm.vcpus,
                networks: vm.network_interfaces || []
              }))
            };
          },
          critical: true
        },
        {
          id: 'terminal-connect',
          name: '터미널 연결 테스트',
          description: '첫 번째 실행 중인 VM에 터미널 연결 시도',
          function: async () => {
            const vms = await apiService.getVMs(false);
            const runningVMs = vms.data.vms.filter(vm => vm.state === 1 || vm.state === 'running');
            
            if (runningVMs.length === 0) {
              throw new Error('실행 중인 VM이 없습니다. 터미널 연결 테스트를 위해 VM을 먼저 시작해주세요.');
            }
            
            const testVM = runningVMs[0];
            const connectResult = await apiService.connectVMTerminal(testVM.name);
            
            // 연결 후 즉시 해제 (테스트용)
            if (connectResult.data.tunnel_id) {
              await apiService.disconnectTerminal(connectResult.data.tunnel_id);
            }
            
            return {
              vm_name: testVM.name,
              tunnel_id: connectResult.data.tunnel_id,
              message: connectResult.data.message,
              test_status: 'connection_successful_and_disconnected'
            };
          },
          critical: true
        },
        {
          id: 'terminal-status',
          name: '터미널 상태 조회',
          description: '현재 활성 터미널 연결 상태 확인',
          function: async () => {
            const terminals = await apiService.listTerminals();
            return {
              active_terminals: Object.keys(terminals.data.tunnels).length,
              tunnels: terminals.data.tunnels
            };
          },
          critical: false
        },
        {
          id: 'terminal-websocket',
          name: 'WebSocket 지원 확인',
          description: 'WebSocket 프록시 설정 확인',
          function: async () => {
            // WebSocket 연결 가능성 테스트 (실제 연결은 하지 않음)
            // PUBLIC_URL을 고려한 WebSocket URL
            const publicUrl = process.env.PUBLIC_URL || '';
            const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}${publicUrl}/ws/terminal/test`;
            
            return {
              websocket_url: wsUrl,
              protocol_support: 'WebSocket' in window,
              nginx_proxy: window.location.pathname.includes('/api/') ? 'configured' : 'direct',
              test_note: 'WebSocket 프록시 설정이 올바르게 구성되어 있는지 확인'
            };
          },
          critical: false
        },
        {
          id: 'terminal-performance',
          name: '터미널 성능 테스트',
          description: '터미널 연결 응답 시간 측정',
          function: async () => {
            const vms = await apiService.getVMs(false);
            const runningVMs = vms.data.vms.filter(vm => vm.state === 1 || vm.state === 'running');
            
            if (runningVMs.length === 0) {
              return { message: '실행 중인 VM이 없어 성능 테스트를 건너뜁니다.' };
            }
            
            const testVM = runningVMs[0];
            const startTime = performance.now();
            
            try {
              const connectResult = await apiService.connectVMTerminal(testVM.name);
              const connectTime = performance.now() - startTime;
              
              // 상태 조회 시간 측정
              const statusStartTime = performance.now();
              const statusResult = await apiService.getTerminalStatus(connectResult.data.tunnel_id);
              const statusTime = performance.now() - statusStartTime;
              
              // 연결 해제 시간 측정
              const disconnectStartTime = performance.now();
              await apiService.disconnectTerminal(connectResult.data.tunnel_id);
              const disconnectTime = performance.now() - disconnectStartTime;
              
              return {
                vm_name: testVM.name,
                connect_time_ms: Math.round(connectTime),
                status_query_time_ms: Math.round(statusTime),
                disconnect_time_ms: Math.round(disconnectTime),
                total_time_ms: Math.round(connectTime + statusTime + disconnectTime),
                performance_rating: connectTime < 1000 ? 'excellent' : connectTime < 3000 ? 'good' : 'slow'
              };
            } catch (error) {
              return {
                vm_name: testVM.name,
                error: error.message,
                performance_rating: 'failed'
              };
            }
          },
          critical: false
        },
        {
          id: 'terminal-ssh-keys',
          name: 'SSH 키 시스템 확인',
          description: '호스트 SSH 키 생성 및 관리 시스템 테스트',
          function: async () => {
            try {
              // 호스트 SSH 키 상태 확인
              const hostKeyResponse = await fetch('/api/ssh-keys/host', {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                }
              });
              const hostKeyData = await hostKeyResponse.json();
              
              // VM SSH 키 목록 조회
              const vmKeysResponse = await fetch('/api/ssh-keys/vms', {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                }
              });
              const vmKeysData = await vmKeysResponse.json();
              
              return {
                host_key_status: hostKeyData.status,
                host_key_exists: hostKeyData.status === 'exists',
                host_key_created_at: hostKeyData.created_at || null,
                vm_keys_count: Object.keys(vmKeysData.vm_keys || {}).length,
                vm_keys: Object.keys(vmKeysData.vm_keys || {}),
                ssh_system_ready: hostKeyData.status === 'exists'
              };
            } catch (error) {
              return {
                error: error.message,
                ssh_system_ready: false
              };
            }
          },
          critical: true
        },
        {
          id: 'terminal-ssh-keys-check',
          name: 'SSH 키 시스템 확인',
          description: 'SSH 키 관리 시스템 상태 확인',
          function: async () => {
            try {
              // 호스트 SSH 키 확인
              const hostKeyResponse = await fetch('/api/ssh-keys/host', {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                }
              });
              const hostKeyData = await hostKeyResponse.json();
              
              // VM SSH 키 목록 확인
              const vmKeysResponse = await fetch('/api/ssh-keys/vms', {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                }
              });
              const vmKeysData = await vmKeysResponse.json();
              
              return {
                host_key_exists: hostKeyData.status === 'exists',
                vm_keys_count: vmKeysData.keys ? vmKeysData.keys.length : 0,
                ssh_keys_system_ready: hostKeyData.status === 'exists'
              };
            } catch (error) {
              return {
                error: error.message,
                ssh_keys_system_ready: false
              };
            }
          },
          critical: true
        },
        {
          id: 'terminal-ssh-key-regen',
          name: 'SSH 키 재생성',
          description: 'DSA 키 문제 해결을 위한 호스트 SSH 키 강제 재생성',
          function: async () => {
            try {
              // 호스트 SSH 키 강제 재생성
              const regenResponse = await fetch('/api/ssh-keys/host/regenerate', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                }
              });
              const regenData = await regenResponse.json();
              
              return {
                regeneration_successful: regenResponse.ok,
                message: regenData.message || '키 재생성 완료',
                public_key_preview: regenData.public_key ? regenData.public_key.substring(0, 50) + '...' : null,
                key_type: regenData.public_key ? regenData.public_key.split(' ')[0] : null
              };
            } catch (error) {
              return {
                error: error.message,
                regeneration_successful: false
              };
            }
          },
          critical: false
        }
      ]
    }
  };

  useEffect(() => {
    loadSystemStatus();
  }, []);

  const loadSystemStatus = async () => {
    setLoading(true);
    try {
      const results = {};
      
      // 기본 상태 체크
      try {
        // PUBLIC_URL을 고려한 health 엔드포인트
        const publicUrl = process.env.PUBLIC_URL || '';
        const response = await fetch(`${publicUrl}/health`);
        const text = await response.text();
        let health;
        try {
          health = JSON.parse(text);
        } catch {
          health = { status: text, libvirt: 'unknown' };
        }
        results.health = { success: true, data: health };
      } catch (error) {
        results.health = { success: false, error: error.message };
      }

      // 각 카테고리의 첫 번째 중요 테스트만 실행
      for (const [categoryKey, category] of Object.entries(testCategories)) {
        const criticalTest = category.tests.find(t => t.critical);
        if (criticalTest) {
          try {
            const result = await criticalTest.function();
            results[categoryKey] = { success: true, data: result };
          } catch (error) {
            results[categoryKey] = { success: false, error: error.message };
          }
        }
      }

      setSystemStatus(results);
    } catch (error) {
      enqueueSnackbar('시스템 상태 로딩 실패: ' + error.message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const runSingleTest = async (categoryKey, test) => {
    const testKey = `${categoryKey}-${test.id}`;
    setTestResults(prev => ({
      ...prev,
      [testKey]: { running: true }
    }));

    try {
      const startTime = Date.now();
      const result = await test.function();
      const duration = Date.now() - startTime;
      
      setTestResults(prev => ({
        ...prev,
        [testKey]: {
          success: true,
          data: result,
          duration,
          timestamp: new Date().toLocaleString()
        }
      }));
      
      enqueueSnackbar(`${test.name} 테스트 성공`, { variant: 'success' });
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        [testKey]: {
          success: false,
          error: error.message,
          timestamp: new Date().toLocaleString()
        }
      }));
      
      enqueueSnackbar(`${test.name} 테스트 실패: ${error.message}`, { variant: 'error' });
    }
  };

  const runCategoryTests = async (categoryKey) => {
    const category = testCategories[categoryKey];
    for (const test of category.tests) {
      await runSingleTest(categoryKey, test);
      // 테스트 간 간격
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  const runAllTests = async () => {
    setLoading(true);
    try {
      for (const categoryKey of Object.keys(testCategories)) {
        await runCategoryTests(categoryKey);
      }
      enqueueSnackbar('모든 테스트 완료', { variant: 'success' });
    } finally {
      setLoading(false);
    }
  };

  const runIntegrationTest = async () => {
    setLoading(true);
    const steps = [];
    
    try {
      // 1. 네트워크 생성
      steps.push('네트워크 생성');
      const networkData = {
        name: integrationTestData.networkName,
        network_type: 'bridge',
        cidr: '192.168.250.0/24',
        description: 'Integration test network'
      };
      await apiService.createNetwork(networkData);
      
      // 2. 네트워크 목록 확인
      steps.push('네트워크 목록 확인');
      const networks = await apiService.getNetworks();
      const createdNetwork = networks.data.networks.find(n => n.name === integrationTestData.networkName);
      
      if (!createdNetwork) {
        throw new Error('생성한 네트워크를 찾을 수 없습니다');
      }

      // 3. 기존 VM이 있다면 네트워크 할당 테스트
      steps.push('VM 목록 확인');
      const vms = await apiService.getVMs(true);
      if (vms.data.vms.length > 0) {
        const testVm = vms.data.vms[0];
        
        steps.push(`VM ${testVm.name}에 네트워크 할당`);
        await apiService.assignVMToNetwork(testVm.name, {
          network_profile: integrationTestData.networkName
        });
        
        steps.push('VM 네트워크 정보 확인');
        const vmNetworks = await apiService.getVMNetworks(testVm.name);
        
        steps.push(`VM ${testVm.name}에서 네트워크 해제`);
        await apiService.unassignVMFromNetwork(testVm.name, integrationTestData.networkName);
      }

      // 4. 네트워크 삭제
      steps.push('테스트 네트워크 삭제');
      await apiService.deleteNetwork(integrationTestData.networkName);
      
      enqueueSnackbar('통합 테스트 성공!', { variant: 'success' });
      setIntegrationTestDialogOpen(false);
      
    } catch (error) {
      enqueueSnackbar(`통합 테스트 실패 (${steps[steps.length - 1]}): ${error.response?.data?.detail || error.message}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const runTerminalIntegrationTest = async () => {
    setLoading(true);
    const steps = [];
    
    try {
      // 1. 실행 중인 VM 확인
      steps.push('실행 중인 VM 확인');
      const vms = await apiService.getVMs(false);
      const runningVMs = vms.data.vms.filter(vm => vm.state === 1 || vm.state === 'running');
      
      if (runningVMs.length === 0) {
        throw new Error('실행 중인 VM이 없습니다. 터미널 통합 테스트를 위해 VM을 먼저 시작해주세요.');
      }
      
      const testVM = integrationTestData.terminalTestVM || runningVMs[0].name;
      const selectedVM = runningVMs.find(vm => vm.name === testVM) || runningVMs[0];
      
      // 2. 터미널 연결 생성
      steps.push(`VM ${selectedVM.name}에 터미널 연결`);
      const connectResult = await apiService.connectVMTerminal(selectedVM.name);
      const tunnelId = connectResult.data.tunnel_id;
      
      if (!tunnelId) {
        throw new Error('터미널 연결 실패: tunnel_id를 받지 못했습니다');
      }
      
      // 3. 터미널 상태 확인
      steps.push('터미널 연결 상태 확인');
      const statusResult = await apiService.getTerminalStatus(tunnelId);
      
      if (statusResult.data.status !== 'active') {
        throw new Error(`터미널 상태가 비정상입니다: ${statusResult.data.status}`);
      }
      
      // 4. 터미널 목록에서 확인
      steps.push('터미널 목록에서 연결 확인');
      const terminalsResult = await apiService.listTerminals();
      const foundTunnel = terminalsResult.data.tunnels[tunnelId];
      
      if (!foundTunnel) {
        throw new Error('터미널 목록에서 연결을 찾을 수 없습니다');
      }
      
      // 5. WebSocket 연결 테스트
      steps.push('WebSocket 연결 테스트');
      // PUBLIC_URL을 고려한 WebSocket URL
      const publicUrl = process.env.PUBLIC_URL || '';
      const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}${publicUrl}/ws/terminal/${tunnelId}`;
      
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          ws.close();
          reject(new Error('WebSocket 연결 타임아웃'));
        }, 5000);
        
        const ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          clearTimeout(timeout);
          ws.close();
          resolve();
        };
        
        ws.onerror = (error) => {
          clearTimeout(timeout);
          reject(new Error('WebSocket 연결 실패'));
        };
      });
      
      // 6. 터미널 연결 해제
      steps.push('터미널 연결 해제');
      await apiService.disconnectTerminal(tunnelId);
      
      // 7. 연결 해제 확인
      steps.push('터미널 연결 해제 확인');
      try {
        await apiService.getTerminalStatus(tunnelId);
        // 연결이 해제되었다면 404 오류가 발생해야 함
        throw new Error('터미널이 정상적으로 해제되지 않았습니다');
      } catch (error) {
        if (error.response?.status === 404 || error.message.includes('not_found')) {
          // 정상적으로 해제됨
        } else {
          throw error;
        }
      }
      
      enqueueSnackbar('터미널 통합 테스트 성공!', { variant: 'success' });
      
    } catch (error) {
      enqueueSnackbar(`터미널 통합 테스트 실패 (${steps[steps.length - 1]}): ${error.response?.data?.detail || error.message}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const openPortForwardDiagnose = async () => {
    setPortForwardDiagOpen(true);
    setPortForwardDiagLoading(true);
    setPortForwardDiagError(null);
    setPortForwardDiagResult(null);
    try {
      const res = await apiService.diagnosePortForwarding();
      setPortForwardDiagResult(res.data);
      const issues = res.data?.total_issues || 0;
      if (issues === 0) {
        enqueueSnackbar('포트 포워딩 진단: 이슈 없음', { variant: 'success' });
      } else {
        enqueueSnackbar(`포트 포워딩 진단: 이슈 ${issues}건 발견`, { variant: 'warning' });
      }
    } catch (error) {
      const msg = error.response?.data?.detail || error.message;
      setPortForwardDiagError(msg);
      enqueueSnackbar(`포트 포워딩 진단 실패: ${msg}`, { variant: 'error' });
    } finally {
      setPortForwardDiagLoading(false);
    }
  };

  const renderRuleRows = (rules) => {
    if (!rules || rules.length === 0) return null;
    return (
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }}>#</TableCell>
              <TableCell>VM</TableCell>
              <TableCell>Proto</TableCell>
              <TableCell>Host Port</TableCell>
              <TableCell>VM IP:Port</TableCell>
              <TableCell>Chain</TableCell>
              <TableCell>설명</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.map((r, i) => (
              <TableRow key={i}>
                <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                <TableCell>{r.vm_name || '-'}</TableCell>
                <TableCell>{r.protocol || '-'}</TableCell>
                <TableCell>{r.host_port ?? '-'}</TableCell>
                <TableCell>{r.vm_ip ? `${r.vm_ip}:${r.vm_port ?? '-'}` : (r.vm_port ?? '-')}</TableCell>
                <TableCell>{r.chain || 'PREROUTING'}</TableCell>
                <TableCell>{r.description || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const getStatusChip = (status) => {
    if (!status) return <Chip label="미실행" color="default" size="small" />;
    
    if (status.success) {
      return <Chip icon={<SuccessIcon />} label="정상" color="success" size="small" />;
    } else {
      return <Chip icon={<ErrorIcon />} label="오류" color="error" size="small" />;
    }
  };

  const getTestResultChip = (result) => {
    if (!result) return <Chip label="대기" color="default" size="small" />;
    if (result.running) return <Chip label="실행중" color="primary" size="small" />;
    
    if (result.success) {
      return (
        <Tooltip title={`소요시간: ${result.duration}ms`}>
          <Chip icon={<SuccessIcon />} label="성공" color="success" size="small" />
        </Tooltip>
      );
    } else {
      return (
        <Tooltip title={result.error}>
          <Chip icon={<ErrorIcon />} label="실패" color="error" size="small" />
        </Tooltip>
      );
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SpeedIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              시스템 기능 테스트
            </Typography>
          </Box>
          <Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadSystemStatus}
              sx={{ mr: 1 }}
            >
              상태 새로고침
            </Button>
            <Button
              variant="contained"
              startIcon={<PlayIcon />}
              onClick={runAllTests}
              disabled={loading}
            >
              전체 테스트 실행
            </Button>
          </Box>
        </Box>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* 시스템 상태 개요 */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {Object.entries(testCategories).map(([key, category]) => (
            <Grid item xs={12} md={6} lg={4} key={key}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {React.cloneElement(category.icon, { 
                      sx: { mr: 2, fontSize: 32 }, 
                      color: category.color 
                    })}
                    <Typography variant="h6">{category.name}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {getStatusChip(systemStatus[key])}
                    <Button
                      size="small"
                      onClick={() => runCategoryTests(key)}
                      disabled={loading}
                    >
                      테스트 실행
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* 상세 테스트 결과 */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              상세 테스트 결과
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {Object.entries(testCategories).map(([categoryKey, category]) => (
              <Accordion key={categoryKey}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    {React.cloneElement(category.icon, { sx: { mr: 2 } })}
                    <Typography sx={{ flexGrow: 1 }}>{category.name}</Typography>
                    {getStatusChip(systemStatus[categoryKey])}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>테스트명</TableCell>
                          <TableCell>설명</TableCell>
                          <TableCell>상태</TableCell>
                          <TableCell>작업</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {category.tests.map((test) => {
                          const testKey = `${categoryKey}-${test.id}`;
                          const result = testResults[testKey];
                          
                          return (
                            <TableRow key={test.id}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  {test.critical && (
                                    <Tooltip title="중요 테스트">
                                      <WarningIcon sx={{ mr: 1, color: 'warning.main', fontSize: 16 }} />
                                    </Tooltip>
                                  )}
                                  {test.name}
                                </Box>
                              </TableCell>
                              <TableCell>{test.description}</TableCell>
                              <TableCell>{getTestResultChip(result)}</TableCell>
                              <TableCell>
                                <Button
                                  size="small"
                                  onClick={() => runSingleTest(categoryKey, test)}
                                  disabled={loading || result?.running}
                                >
                                  실행
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {/* 테스트 결과 상세 표시 */}
                  {category.tests.map((test) => {
                    const testKey = `${categoryKey}-${test.id}`;
                    const result = testResults[testKey];
                    
                    if (!result || result.running) return null;
                    
                    return (
                      <Box key={test.id} sx={{ mt: 2 }}>
                        <Typography variant="subtitle2">{test.name} 결과:</Typography>
                        {result.success ? (
                          <Alert severity="success" sx={{ mt: 1 }}>
                            성공 - 소요시간: {result.duration}ms ({result.timestamp})
                            {result.data?.data && (
                              <pre style={{ fontSize: '0.8em', marginTop: '8px', overflow: 'auto' }}>
                                {JSON.stringify(result.data.data, null, 2).substring(0, 500)}
                                {JSON.stringify(result.data.data, null, 2).length > 500 && '...'}
                              </pre>
                            )}
                          </Alert>
                        ) : (
                          <Alert severity="error" sx={{ mt: 1 }}>
                            실패: {result.error} ({result.timestamp})
                          </Alert>
                        )}
                      </Box>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            ))}
          </CardContent>
        </Card>

        {/* 통합 테스트 */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              통합 테스트
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  네트워크 통합 테스트
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  네트워크 생성 → VM 할당 → 해제 → 삭제의 전체 워크플로우를 테스트합니다.
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setIntegrationTestDialogOpen(true)}
                  disabled={loading}
                  fullWidth
                >
                  네트워크 통합 테스트
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  터미널 통합 테스트
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  터미널 연결 → 상태 확인 → 목록 조회 → 연결 해제의 전체 워크플로우를 테스트합니다.
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setTerminalIntegrationDialogOpen(true)}
                  disabled={loading}
                  fullWidth
                >
                  터미널 통합 테스트
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  포트 포워딩 룰 진단
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  PREROUTING DNAT, FORWARD ACCEPT, DOCKER-USER ACCEPT 체인 간 일관성과 중복/충돌 규칙을 점검합니다.
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<BuildIcon />}
                  onClick={openPortForwardDiagnose}
                  disabled={loading || portForwardDiagLoading}
                  fullWidth
                >
                  {portForwardDiagLoading ? '진단 중...' : '포트 포워딩 진단 실행'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* 네트워크 통합 테스트 다이얼로그 */}
        <Dialog open={integrationTestDialogOpen} onClose={() => setIntegrationTestDialogOpen(false)}>
          <DialogTitle>네트워크 통합 테스트 설정</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="테스트 네트워크 이름"
              value={integrationTestData.networkName}
              onChange={(e) => setIntegrationTestData(prev => ({
                ...prev,
                networkName: e.target.value
              }))}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIntegrationTestDialogOpen(false)}>취소</Button>
            <Button 
              onClick={runIntegrationTest} 
              variant="contained"
              disabled={loading || !integrationTestData.networkName}
            >
              테스트 시작
            </Button>
          </DialogActions>
        </Dialog>

        {/* 터미널 통합 테스트 다이얼로그 */}
        <Dialog open={terminalIntegrationDialogOpen} onClose={() => setTerminalIntegrationDialogOpen(false)}>
          <DialogTitle>터미널 통합 테스트 설정</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
              실행 중인 VM에 터미널 연결을 테스트합니다. 특정 VM을 지정하거나 자동으로 첫 번째 실행 중인 VM을 사용할 수 있습니다.
            </Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>테스트 대상 VM (선택사항)</InputLabel>
              <Select
                value={integrationTestData.terminalTestVM}
                label="테스트 대상 VM (선택사항)"
                onChange={(e) => setIntegrationTestData(prev => ({
                  ...prev,
                  terminalTestVM: e.target.value
                }))}
              >
                <MenuItem value="">
                  <em>자동 선택 (첫 번째 실행 중인 VM)</em>
                </MenuItem>
                {/* VM 목록은 동적으로 로드될 수 있지만 여기서는 간단히 처리 */}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="테스트 VM 이름 (직접 입력)"
              value={integrationTestData.terminalTestVM}
              onChange={(e) => setIntegrationTestData(prev => ({
                ...prev,
                terminalTestVM: e.target.value
              }))}
              sx={{ mt: 2 }}
              placeholder="비워두면 자동으로 첫 번째 실행 중인 VM 사용"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTerminalIntegrationDialogOpen(false)}>취소</Button>
            <Button
              onClick={() => {
                runTerminalIntegrationTest();
                setTerminalIntegrationDialogOpen(false);
              }}
              variant="contained"
              disabled={loading}
            >
              테스트 시작
            </Button>
          </DialogActions>
        </Dialog>

        {/* 포트 포워딩 진단 결과 다이얼로그 */}
        <Dialog
          open={portForwardDiagOpen}
          onClose={() => setPortForwardDiagOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <RouterIcon sx={{ mr: 1 }} />
              포트 포워딩 룰 진단 결과
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {portForwardDiagLoading && (
              <Box sx={{ py: 4 }}>
                <LinearProgress />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  iptables 체인을 점검 중...
                </Typography>
              </Box>
            )}

            {!portForwardDiagLoading && portForwardDiagError && (
              <Alert severity="error">{portForwardDiagError}</Alert>
            )}

            {!portForwardDiagLoading && portForwardDiagResult && (() => {
              const d = portForwardDiagResult;
              const issues = d.issues || {};
              const counts = d.counts || {};
              const env = d.environment || {};
              const totalIssues = d.total_issues || 0;

              const section = (title, rules, severity) => {
                if (!rules || rules.length === 0) return null;
                return (
                  <Box sx={{ mb: 3 }}>
                    <Alert severity={severity || 'warning'} sx={{ mb: 1 }}>
                      <strong>{title}</strong> — {rules.length}건
                    </Alert>
                    {renderRuleRows(rules)}
                  </Box>
                );
              };

              return (
                <>
                  {/* 요약 */}
                  <Box sx={{ mb: 2 }}>
                    {totalIssues === 0 ? (
                      <Alert severity="success" icon={<SuccessIcon />}>
                        이슈가 발견되지 않았습니다. 모든 체인이 일관된 상태입니다.
                      </Alert>
                    ) : (
                      <Alert severity="warning" icon={<WarningIcon />}>
                        총 <strong>{totalIssues}</strong>건의 이슈가 발견되었습니다.
                      </Alert>
                    )}
                  </Box>

                  {/* 호스트 환경 정보 */}
                  <Box sx={{ mb: 2 }}>
                    <Alert severity={env.current_external_interface ? 'info' : 'warning'} sx={{ mb: 1 }}>
                      <Typography variant="body2">
                        <strong>현재 호스트 외부 인터페이스</strong>:{' '}
                        {env.current_external_interface
                          ? <code>{env.current_external_interface}</code>
                          : '감지 실패 — PREROUTING 룰에 -i가 박히지 않거나 모든 인터페이스가 매칭됩니다'}
                      </Typography>
                      {env.host_interfaces && env.host_interfaces.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          호스트 NIC 목록: {env.host_interfaces.join(', ')}
                        </Typography>
                      )}
                    </Alert>
                  </Box>

                  {/* 카운트 통계 */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} md={3}>
                      <Card variant="outlined">
                        <CardContent sx={{ py: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">PREROUTING DNAT</Typography>
                          <Typography variant="h5">{counts.prerouting ?? 0}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Card variant="outlined">
                        <CardContent sx={{ py: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">FORWARD ACCEPT</Typography>
                          <Typography variant="h5">{counts.forward_accept ?? 0}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Card variant="outlined">
                        <CardContent sx={{ py: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">DOCKER-USER ACCEPT</Typography>
                          <Typography variant="h5">{counts.docker_user_accept ?? 0}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            체인 존재: {counts.docker_user_chain_exists ? 'O' : 'X'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Card variant="outlined" sx={{ borderColor: totalIssues > 0 ? 'warning.main' : 'success.main' }}>
                        <CardContent sx={{ py: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">발견된 이슈</Typography>
                          <Typography variant="h5" color={totalIssues > 0 ? 'warning.main' : 'success.main'}>
                            {totalIssues}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* 이슈 상세 */}
                  {section(
                    'FORWARD에 ACCEPT가 빠진 PREROUTING (트래픽 차단 가능)',
                    issues.missing_in_forward,
                    'error'
                  )}
                  {section(
                    'PREROUTING 없이 FORWARD에만 남은 ACCEPT (잔재)',
                    issues.orphan_in_forward,
                    'warning'
                  )}
                  {section(
                    'DOCKER-USER에 ACCEPT가 빠진 PREROUTING (Docker 재시작 시 끊김 위험)',
                    issues.missing_in_docker_user,
                    'warning'
                  )}
                  {section(
                    'PREROUTING 없이 DOCKER-USER에만 남은 ACCEPT (잔재)',
                    issues.orphan_in_docker_user,
                    'info'
                  )}

                  {/* 중복 ACCEPT */}
                  {issues.duplicate_accepts && issues.duplicate_accepts.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        <strong>같은 (chain, vm_ip, vm_port, protocol)에 ACCEPT 룰 중복</strong> — {issues.duplicate_accepts.length}건
                      </Alert>
                      {issues.duplicate_accepts.map((g, gi) => (
                        <Box key={gi} sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>{g.chain}</strong> · {g.vm_ip}:{g.vm_port} · {g.protocol} · {g.count}회 중복
                          </Typography>
                          {renderRuleRows(g.rules)}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* cross-VM 충돌 */}
                  {issues.cross_vm_host_port_collisions && issues.cross_vm_host_port_collisions.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Alert severity="error" sx={{ mb: 1 }}>
                        <strong>같은 host_port가 여러 VM으로 DNAT (silent 충돌)</strong> — {issues.cross_vm_host_port_collisions.length}건
                      </Alert>
                      {issues.cross_vm_host_port_collisions.map((g, gi) => (
                        <Box key={gi} sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>{g.protocol}/{g.host_port}</strong> · VMs: {g.vm_names?.join(', ')} · {g.count}건
                          </Typography>
                          {renderRuleRows(g.rules)}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* 인터페이스 mismatch */}
                  {issues.interface_mismatches && issues.interface_mismatches.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Alert severity="error" sx={{ mb: 1 }}>
                        <strong>PREROUTING -i 인터페이스가 현재 호스트 외부 NIC와 다름</strong>
                        {' '}— {issues.interface_mismatches.length}건
                        <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                          (DNAT가 매칭되지 않아 외부 패킷이 그대로 떨어집니다. 보통 컨테이너 내부에서
                          인터페이스를 잘못 감지한 경우이며, IP 동기화를 한 번 돌리면 새 NIC로 재기록됩니다.)
                        </Typography>
                      </Alert>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ width: 40 }}>#</TableCell>
                              <TableCell>VM</TableCell>
                              <TableCell>Host Port</TableCell>
                              <TableCell>룰에 박힌 -i</TableCell>
                              <TableCell>호스트에 존재?</TableCell>
                              <TableCell>현재 외부 NIC</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {issues.interface_mismatches.map((m, i) => (
                              <TableRow key={i}>
                                <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                                <TableCell>{m.vm_name}</TableCell>
                                <TableCell>{m.protocol}/{m.host_port}</TableCell>
                                <TableCell>
                                  <code>{m.rule_interface}</code>
                                </TableCell>
                                <TableCell>
                                  {m.rule_interface_exists_on_host == null
                                    ? '-'
                                    : m.rule_interface_exists_on_host ? 'O' : 'X (없음)'}
                                </TableCell>
                                <TableCell>
                                  <code>{m.current_external_interface}</code>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </>
              );
            })()}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={openPortForwardDiagnose}
              startIcon={<RefreshIcon />}
              disabled={portForwardDiagLoading}
            >
              다시 진단
            </Button>
            <Button onClick={() => setPortForwardDiagOpen(false)} variant="contained">
              닫기
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default SystemTestPage; 